import {AutoFocusModule} from 'primeng/autofocus';
import {BlockUIModule} from 'primeng/blockui';
import {ButtonModule} from 'primeng/button';
import {DynamicDialogRef} from 'primeng/dynamicdialog';
import {FloatLabelModule} from 'primeng/floatlabel';
import {IconFieldModule} from 'primeng/iconfield';
import {InputGroupModule} from 'primeng/inputgroup';
import {InputGroupAddonModule} from 'primeng/inputgroupaddon';
import {InputIconModule} from 'primeng/inputicon';
import {InputTextModule} from 'primeng/inputtext';
import {PanelModule} from 'primeng/panel';
import {PasswordModule} from 'primeng/password';
import {catchError, finalize, throwError} from 'rxjs';

import {HttpErrorResponse} from '@angular/common/http';
import {ChangeDetectionStrategy, Component, inject, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatDivider} from '@angular/material/divider';
import {ILoginResponse} from '@core/api/data-access';
import {TranslateModule} from '@ngx-translate/core';
import {environment} from '@shared-types';

import {LoginRequestModel} from '../../../models/login/login-request.model';
import {AuthStore} from '../../../+store/auth.store';
import {ActiveUserStore} from '@active-user-management';

@Component({
	selector: 'dc-re-authenticate-dialog',
	imports: [
		PanelModule,
		TranslateModule,
		PasswordModule,
		ButtonModule,
		IconFieldModule,
		InputIconModule,
		InputTextModule,
		FloatLabelModule,
		InputGroupModule,
		InputGroupAddonModule,
		FormsModule,
		AutoFocusModule,
		MatDivider,
		BlockUIModule,
	],
	templateUrl: './re-authenticate-dialog.component.html',
	styleUrl: './re-authenticate-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReAuthenticateDialogComponent {
	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	private readonly authStore: AuthStore = inject(AuthStore);
	private readonly dynamicDialogRef = inject(DynamicDialogRef);
	readonly activeUserStore: ActiveUserStore = inject(ActiveUserStore);

	/* ------------------------------------------ Signals ------------------------------------------ */
	protected readonly activeUser$ = this.activeUserStore.user$;

	/* ------------------------------------------ Data ------------------------------------------ */
	protected readonly inLoginProcess$: WritableSignal<boolean> = signal(false);
	protected readonly error$: WritableSignal<string> = signal(null);
	protected readonly password$: WritableSignal<string> = signal('');

	/* ------------------------------------------ Constants ------------------------------------------ */
	protected readonly direction: 'right' | 'left' = document.dir === 'rtl' ? 'left' : 'right';

	/* ----------------------------------------------  Constructor ---------------------------------------------- */
	constructor() {}

	/* ---------------------------------------------- Methods ---------------------------------------------- */
	protected reAuthenticate(): void {
		this.inLoginProcess$.set(true);
		const reLoginRequest: LoginRequestModel = new LoginRequestModel({
			company: environment.company,
			userName: this.activeUser$().username,
			password: this.password$(),
		});
		this.authStore
			.login(reLoginRequest)
			.pipe(
				finalize(() => {
					this.inLoginProcess$.set(false);
				}),
				catchError((error: HttpErrorResponse) => {
					if (error && error?.error?.error === 'no match') {
						this.error$.set('pages.login.re-authenticate-dialog.no-match');
					}
					this.inLoginProcess$.set(false);
					return throwError(() => error);
				}),
			)
			.subscribe((response: ILoginResponse) => {
				this.inLoginProcess$.set(false);
				this.dynamicDialogRef.close(response);
			});
	}

	protected cancelReAuthenticate(): void {
		this.dynamicDialogRef.close(null);
	}
}
